import {
  Center,
  Table,
  TableContainer,
  TableProps,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { AfeTransactionSummaryResponse } from 'api/bindings/AfeTransactionSummaryResponse';
import { SubAccountResponse } from 'api/bindings/SubAccountResponse';
import Decimal from 'decimal.js';
import { CurrencyLabel, SubAccountLabel } from 'features/components';
import { useMemo } from 'react';

interface Entry {
  sa: SubAccountResponse;
  pendingBudget: Decimal;
  pendingContingency: Decimal;
  totalBudget: Decimal;
  totalContingency: Decimal;
}

export interface AfeBudgetSummaryTableProps extends TableProps {
  txns: AfeTransactionSummaryResponse[] | undefined;
}

export const AfeBudgetSummaryTable = (props: AfeBudgetSummaryTableProps) => {
  const { txns = [], ...other } = props;

  const summary = useMemo(() => {
    const results = txns.reduce((sum, n) => {
      return n.lineItems.reduce((sum2, n2) => {
        const entry = sum2[Number(n2.subAccountId)] ?? {
          sa: n2.subAccount,
          totalBudget: new Decimal(0),
          totalContingency: new Decimal(0),
          pendingBudget: new Decimal(0),
          pendingContingency: new Decimal(0),
        };

        if (n.redeemed) {
          entry.totalBudget = entry.pendingBudget.add(n2.budgetedAmount);
          entry.totalContingency = entry.pendingContingency.add(
            n2.contingencyAmount
          );
        } else if (!n.cancelled) {
          entry.pendingBudget = entry.pendingBudget.add(n2.budgetedAmount);
          entry.pendingContingency = entry.pendingContingency.add(
            n2.contingencyAmount
          );
        }

        sum2[Number(n2.subAccountId)] = entry;
        return sum2;
      }, sum);
    }, {} as Record<number, Entry>);
    return Object.keys(results).map((k) => results[parseInt(k)]);
  }, [txns]);

  return (
    <TableContainer>
      <Table variant={'simple'} {...other}>
        <Thead>
          <Tr>
            <Th>Account</Th>
            <Th>Pending Budget</Th>
            <Th>Pending Contingency</Th>
            <Th>Approved Budget</Th>
            <Th>Approved Contingency</Th>
          </Tr>
        </Thead>
        <Tbody>
          {!summary.length && (
            <Tr>
              <Td colSpan={5}>
                <Center>No Accounts to Display</Center>
              </Td>
            </Tr>
          )}
          {summary.map((li, i) => (
            <Tr key={i}>
              <Td>
                <SubAccountLabel value={li.sa} />
              </Td>
              <Td>
                <CurrencyLabel value={li.pendingBudget} />
              </Td>
              <Td>
                <CurrencyLabel value={li.pendingContingency} />
              </Td>
              <Td>
                <CurrencyLabel value={li.totalBudget} />
              </Td>
              <Td>
                <CurrencyLabel value={li.totalContingency} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
